/*
 * GroundSchoolPromo — homepage callout for the Advanced RPAS Ground School.
 *
 * Placement: between the Services grid and the Process section, framed as a
 * complementary offering to the geospatial services (dark brand palette,
 * teal accent, cinematic image, monospace overline). Deep-links straight
 * into /groundschool. Kept intentionally compact — not a hero, not a full
 * landing block — so the marketing site's main lead-gen flow still
 * dominates. Homepage focus is quotes; this exists to make the course
 * discoverable, not to convert on the homepage itself.
 */
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Award, CheckCircle2 } from "lucide-react";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_tbirdgeo-preview/artifacts/nqvumnjg_IMG_5588-3.png";

const BENEFITS = [
  "8 modules · ~30 study hours",
  "50-question practice exams",
  "Certificate of Completion",
  "Lifetime access · no renewals",
];

export const GroundSchoolPromo = () => {
  return (
    <section
      id="groundschool-promo"
      data-testid="homepage-groundschool-promo"
      className="relative py-24 md:py-28 bg-black"
    >
      {/* Subtle top divider to separate from Services */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#3DC0AF]/25 to-transparent" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-14 items-stretch"
        >
          {/* Left: image */}
          <div className="relative overflow-hidden rounded-2xl border border-white/10 min-h-[280px] lg:min-h-[380px]">
            <img
              src={HERO_IMG}
              alt="Advanced RPAS drone in flight — Thunderbird Geospatial Ground School"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
              data-testid="homepage-gs-promo-image"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-black/70 via-black/20 to-transparent" />
            {/* Corner overline */}
            <div className="absolute top-5 left-5 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#3DC0AF] animate-pulse" />
              <span className="font-mono text-[10px] tracking-[0.28em] text-white uppercase">
                / Enrolment Open
              </span>
            </div>
            {/* Award badge */}
            <div className="absolute bottom-5 left-5 flex items-center gap-3 rounded-full border border-white/15 bg-black/60 backdrop-blur-md pl-3 pr-4 py-2">
              <Award size={14} className="text-[#3DC0AF]" />
              <span className="font-mono text-[11px] tracking-[0.14em] text-white">
                Transport Canada · Small Advanced Exam Prep
              </span>
            </div>
          </div>

          {/* Right: copy + CTA */}
          <div className="flex flex-col justify-center">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#3DC0AF] font-mono font-bold">
              / Ground School
            </div>
            <h2 className="mt-4 text-4xl sm:text-5xl lg:text-[3.25rem] font-display font-black tracking-tighter leading-[0.98]">
              Fly Advanced.<br />
              <span className="text-[#3DC0AF]">Study on your own terms.</span>
            </h2>
            <p className="mt-5 text-tb-text/80 leading-relaxed text-[15px] max-w-xl">
              Our online Advanced RPAS Ground School is written to the Transport Canada
              knowledge requirements for Advanced Operations — everything you need to
              walk into the Small Advanced Exam prepared. Self-paced, lifetime access,
              designed and taught by working GIS pilots.
            </p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              {BENEFITS.map((b) => (
                <li key={b} className="flex items-start gap-2 text-tb-text/85">
                  <CheckCircle2 size={15} className="text-[#3DC0AF] mt-0.5 flex-shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/groundschool"
                className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#3DC0AF] text-black font-semibold text-[15px] hover:bg-white transition"
                data-testid="homepage-gs-promo-cta"
              >
                Explore the course
                <ArrowUpRight size={17} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <span className="font-mono text-[11px] tracking-[0.14em] text-tb-text/55">
                $249 CAD · One-time · Lifetime access
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default GroundSchoolPromo;
